import { PaymentMethod } from './../typegen/payment-method';
import { makeAutoObservable, runInAction } from 'mobx';
import { UIConfig } from '../typegen/ui-config';
import ftbendDemoConfig from '../../ui-configs/ftbend-config.json';
import icDemoConfig from '../../ui-configs/ic-config.json';
import translationsImport from '../../../translations.json';
import logger from '../helpers/logger';
import LanguageCode, { Translations } from '../types/language';
import { SupportedLanguage } from '../typegen/supported-language';
import enumHelper from '../helpers/enums';
import { store } from './store';
import api from '../api/api';
import { CISConfigType } from '../typegen/cis-config-type';
import { PPConfigType } from '../typegen/pp-config-type';
import { AppEnvironment } from '../typegen/app-environment';
import KioAppHelper from '../helpers/kioApp';
import { UseDeviceOption } from '../types/deviceOption';
import { SupportedDevices } from '../typegen/supported-devices';
import sqlite from '../helpers/sqlite';
import { ReceiptType } from '../typegen/receipt-type';
import { KeyValue } from '../typegen/key-value';
import { CompanyPolicy } from '../typegen/company-policy';
import { applyDocumentLanguage } from '../constants/rtlLanguages';
import { dropCollidingAutoTranslations, sanitizeTranslations } from '../helpers/sanitizeTranslationText';
import { toTranslatorLanguageCode } from '../helpers/translatorLanguageCode';
import { PayGoClientSettings } from '../typegen/pay-go-client-settings';
import { MyUsageClientSettings } from '../typegen/my-usage-client-settings';

const DEFAULT_LANGUAGE = 'en';
const CONFIG_VERSION = 1;
const RETRY_BACKOFF: number = 2;
const RETRY_START_DELAY: number = 1000;

type CachedTranslations = {
	[K in LanguageCode]?: Translations;
};

export default class ConfigStore {
	private _isConfigLoaded = false;
	private _config: UIConfig | null = null;
	private _currentLanguage: LanguageCode = DEFAULT_LANGUAGE;
	private _cachedTranslations: CachedTranslations = {};
	private _currentPageId = '';
	private _pageParams?: KeyValue[];
	private _cisType: CISConfigType | null = null;
	private _cashProcessorType: CISConfigType | null = null;
	private _ppType: PPConfigType | null = null;
	private _kioskConfigEnvironment: AppEnvironment | null = null;
	private _supportedDevices: SupportedDevices = SupportedDevices.None;
	private _receiptOptionsAvailable = ReceiptType.None;
	private _allowMultipleAccountSelection = false;
	private _kioskMethodsOfPayment: PaymentMethod[] = [];
	private _companyPolicy: CompanyPolicy = {
		obscuring: {
			screenItemNumber: false,
			screenCustomerId: false,
			printItemNumber: false,
			printCustomerId: false,
		},
		obscuringOptions: {
			visibleDigits: 4,
			maskCharacter: '*',
			showMaskCharacters: true,
		},
	};
	private _callProviderGroupId: string | null = null;
	private _payGoClientSettings: PayGoClientSettings | null = null;
	private _myUsageClientSettings: MyUsageClientSettings | null = null;

	constructor() {
		makeAutoObservable(this);
	}

	get kioskMethodsOfPayment() {
		return this._kioskMethodsOfPayment;
	}

	get companyPolicy() {
		return this._companyPolicy;
	}

	get callProviderGroupId() {
		return this._callProviderGroupId;
	}

	get payGoClientSettings() {
		return this._payGoClientSettings;
	}

	get myUsageClientSettings() {
		return this._myUsageClientSettings;
	}

	get allowMultipleAccountSelection() {
		return this._allowMultipleAccountSelection;
	}

	get kioskConfigEnvironment() {
		return this._kioskConfigEnvironment;
	}

	get currentLanguage() {
		return this._currentLanguage;
	}

	get currentLanguageLegacyString() {
		return SupportedLanguage[enumHelper.getSupportedLanguage(store.configStore.currentLanguage)] as Exclude<
			keyof typeof SupportedLanguage,
			'None'
		>;
	}

	get cisType() {
		return this._cisType ?? CISConfigType.None;
	}

	/**
	 * The system that actually processes a cash tender, reported as the payment processor on cash rows
	 * (cash never touches a card gateway). Usually the kiosk's own CIS; CSA on a MyUsage kiosk with
	 * CashPostbackToCsa. Falls back to cisType so an older API that omits the field behaves as today.
	 */
	get cashProcessorType() {
		return this._cashProcessorType ?? this.cisType;
	}

	get ppType() {
		return this._ppType ?? PPConfigType.None;
	}

	get supportedDevices() {
		return this._supportedDevices;
	}

	get config() {
		return this._config!;
	}

	get getCustomTimerTimeout() {
		if (this.config.percentToTriggerSessionAlert) return { type: 'percent', value: this.config.percentToTriggerSessionAlert };
		else if (this.config.secondsToTriggerSessionAlert) return { type: 'seconds', value: this.config.secondsToTriggerSessionAlert };
		else return null;
	}

	get companyId() {
		return this.config?.companyId;
	}

	get receiptOptionsAvailable() {
		return this._receiptOptionsAvailable;
	}

	get environment() {
		try {
			if (!this._kioskConfigEnvironment) return null;
			if (this._kioskConfigEnvironment !== AppEnvironment.Dynamic) return this._kioskConfigEnvironment;
			const stringEnv =
				KioAppHelper('DoUrlSubstitution', import.meta.env.VITE_ST_BPK_KIOSK_ENVIRONMENT) ?? import.meta.env.VITE_KIOSK_ENVIRONMENT;
			if (!stringEnv) {
				logger.logGeneral.error(
					{},
					`The kiosk env is set to Dynamic, but the kiosk environment variable is not set on Kioware or on the local env variable, defaulting to null`,
					stringEnv
				);
				return null;
			}
			let env: number | undefined = undefined;
			if (stringEnv) env = parseInt(stringEnv, 10);
			if (typeof env !== 'number' || isNaN(env)) {
				logger.logGeneral.error(
					{},
					`Kiosk environment variable ${stringEnv} is not a valid number, defaulting to null`,
					`Env Value: ${stringEnv}`
				);
				return null;
			}
			return env;
		} catch (error) {
			logger.logGeneral.error({}, `There was an error getting the kiosk environment, defaulting to null`, `Error: ${error}`);
			return null;
		}
	}

	get envDeviceOption(): UseDeviceOption {
		if (!store.configStore.environment) return 'always';

		switch (store.configStore.environment) {
			case AppEnvironment.Demo:
				return 'never';
			case AppEnvironment.Prod:
				return 'always';
			case AppEnvironment.Dev:
			case AppEnvironment.QA:
				return 'with-fallback';
			default:
				return 'always';
		}
	}

	get testReceiptsEnabled(): boolean {
		// Runtime gate via KioWare. KioAppHelper returns null when KioApp isn't available
		// (browser dev), or the literal token '$$BPK_SHOW_TEST_RECEIPTS$$' when the variable
		// isn't configured in KioWare; either case falls through to the build-time env.
		const fromKw = KioAppHelper('DoUrlSubstitution', import.meta.env.VITE_ST_BPK_SHOW_TEST_RECEIPTS);
		if (fromKw && !fromKw.includes('$$')) return fromKw === 'true';
		return import.meta.env.VITE_SHOW_TEST_RECEIPTS === 'true';
	}

	get cashStubMode(): CashStubMode {
		// Runtime override via KioWare. Returns null when KioApp isn't available (browser dev),
		// or the literal token '$$BPK_CASH_STUB_MODE$$' if the variable isn't configured in KioWare;
		// either case falls through to the build-time env.
		const fromKw = KioAppHelper('DoUrlSubstitution', '$$BPK_CASH_STUB_MODE$$');
		if (fromKw === 'prompt' || fromKw === 'random') return fromKw;
		return import.meta.env.VITE_CASH_STUB_MODE === 'prompt' ? 'prompt' : 'random';
	}

	getString = (key?: string, noTranslate: boolean = false) => {
		if (!key) return '';
		const useAmountRemainingLabel =
			!noTranslate &&
			(this.ppType === PPConfigType.PayGo || this.ppType === PPConfigType.MyUsage) &&
			store.accountStore.hasPrePayLookupAccounts &&
			(key === 'payment-amount' || key === 'current-amount' || key === 'account-balance');
		const resolvedKey = useAmountRemainingLabel ? 'amount-remaining' : key;
		let language = this._currentLanguage;
		if (noTranslate || !(this._currentLanguage in this._cachedTranslations)) language = DEFAULT_LANGUAGE;
		if (!(language in this._cachedTranslations)) return key;
		return this._cachedTranslations[language]?.[resolvedKey] ?? this._cachedTranslations[language]?.[key] ?? key;
	};

	/** Cache a single translation at runtime (e.g. for dynamically discovered device messages). */
	setTranslation = (language: LanguageCode, key: string, value: string) => {
		if (!(language in this._cachedTranslations)) this._cachedTranslations[language] = {};
		this._cachedTranslations[language]![key] = value;
	};

	get currentPage() {
		return this.getPage(this.currentPageId);
	}

	get pageParams() {
		return this._pageParams;
	}

	setPageParams = (params?: KeyValue[]) => {
		this._pageParams = params;
	};

	get demoConfig() {
		switch (import.meta.env.VITE_DEMO_CONFIG) {
			case '1':
				return ftbendDemoConfig as unknown as UIConfig;
			case '2':
				return icDemoConfig as UIConfig;
			default:
				return ftbendDemoConfig as unknown as UIConfig;
		}
	}

	get kioskLocation(): string {
		return KioAppHelper('DoUrlSubstitution', '$$KIOSK_LOCATION$$') ?? import.meta.env.VITE_KIOSK_LOCATION ?? '';
	}

	loadConfig = async () => {
		if (import.meta.env.VITE_USE_LOCAL_CONFIG === 'true') {
			await new Promise<void>((resolve) => {
				setTimeout(resolve, 500);
			});
			runInAction(() => {
				this._config = this.demoConfig;
				this._cachedTranslations = translationsImport;
				const firstPage = this._config.pages[0];
				this._currentPageId = firstPage.id;
			});
			await this.changeLanguage(DEFAULT_LANGUAGE);
			runInAction(() => {
				this._isConfigLoaded = true;
			});
			return;
		}

		let retryDelay: number = RETRY_START_DELAY;
		while (true) {
			try {
				const {
					uiConfig,
					cisConfigType,
					cashProcessorType,
					ppConfigType,
					environment,
					supportedDevices,
					receiptOptionsAvailable,
					kioskName,
					kioskState,
					allowMultipleAccountsPerLookup,
					paymentMethods,
					companyPolicy,
					callProviderGroupId,
					payGoClientSettings,
					myUsageClientSettings,
				} = await api.configs.getConfig(Number(store.kioskStore.kioskId), CONFIG_VERSION);
				// Seed the bundled baseline before changeLanguage so the DB merge can
				// overlay kiosk-specific overrides (and so we don't wipe that merge later).
				runInAction(() => {
					this._cachedTranslations = translationsImport;
				});
				await this.changeLanguage(DEFAULT_LANGUAGE);
				await sqlite.init();
				runInAction(() => {
					store.kioskStore.setKioskName(kioskName);
					this._config = uiConfig;
					this._cisType = cisConfigType;
					this._cashProcessorType = cashProcessorType ?? null;
					this._ppType = ppConfigType;
					this._kioskConfigEnvironment = environment;
					this._supportedDevices = supportedDevices;
					this._receiptOptionsAvailable = receiptOptionsAvailable;
					this._isConfigLoaded = true;
					const firstPage = this._config.pages[0];
					this._currentPageId = firstPage.id;
					store.kioskStore.setLastKioskStateOnConfigLoad(kioskState);
					this._allowMultipleAccountSelection = allowMultipleAccountsPerLookup;
					this._kioskMethodsOfPayment = paymentMethods;
					if (companyPolicy) this._companyPolicy = companyPolicy;
					this._callProviderGroupId = callProviderGroupId ?? null;
					this._payGoClientSettings = payGoClientSettings ?? null;
					this._myUsageClientSettings = myUsageClientSettings ?? null;
				});
				break;
			} catch (error) {
				const msg = error instanceof Error ? error.message : error instanceof Array && error.length > 0 ? error[0] : 'Unknown';
				logger.logGeneral.error({}, `Error loading config: ${msg}`, `${error}`);
				await new Promise((resolve) => {
					setTimeout(resolve, retryDelay);
				});
				retryDelay = retryDelay * RETRY_BACKOFF;
			} finally {
				await this.checkAndSetCashPayments();
			}
		}
		logger.debug.warn(`Device options set to: ${this.envDeviceOption}. Current environment: ${AppEnvironment[this.environment ?? -1]}.`);
	};

	private _changeLanguage = async (language: LanguageCode) => {
		const bundled = this._cachedTranslations[language];
		const kioskId = store.kioskStore.kioskId;
		// Demo / local config and missing kiosk id: keep the bundled baseline only.
		const shouldFetchDb = !!kioskId && import.meta.env.VITE_USE_LOCAL_CONFIG !== 'true';

		let translations: Translations | undefined;
		try {
			if (shouldFetchDb) {
				const db = await api.translations.getTranslations(kioskId, language);
				// Per-key merge: DB overrides bundled; bundled fills gaps.
				translations = { ...bundled, ...db };
			} else {
				translations = bundled ?? {};
			}
		} catch (error) {
			const msg = error instanceof Error ? error.message : error instanceof Array && error.length > 0 ? error[0] : 'Unknown';
			logger.logGeneral.error({}, `Error getting translations: ${msg}`, `${error}`);
			// Offline / API-down: fall back to the bundled set. Only fail hard for
			// the default language when there is nothing usable to fall back to.
			if (language === DEFAULT_LANGUAGE && !(bundled && Object.keys(bundled).length > 0)) {
				throw new Error('Failed to get default translations');
			}
			translations = bundled ?? {};
		}

		if (language !== DEFAULT_LANGUAGE) {
			const untranslatedStrings: Translations = {};
			Object.keys(this._cachedTranslations?.[DEFAULT_LANGUAGE] || {})
				.filter((key) => translations && !(key in translations))
				.forEach((key) => (untranslatedStrings[key] = this._cachedTranslations?.[DEFAULT_LANGUAGE]?.[key] || ''));

			if (Object.keys(untranslatedStrings).length > 0) {
				// Azure Translator remaps — see toTranslatorLanguageCode() and docs/INTERNATIONALIZATION_SUPPORT.md
				const additionalTranslations = await this.autoTranslate(untranslatedStrings, toTranslatorLanguageCode(language));
				translations = { ...translations, ...additionalTranslations };
			}

			if (Object.keys(translations || {}).length < Object.keys(this._cachedTranslations?.[DEFAULT_LANGUAGE] || {}).length) {
				logger.logGeneral.error(
					{},
					`Could not get translations for language "${language}".  Using the default language.`,
					`${language} is null or undefined.`
				);
				language = DEFAULT_LANGUAGE;
				translations = this._cachedTranslations[DEFAULT_LANGUAGE];
			}
		}

		const effectiveLanguage = language;
		applyDocumentLanguage(effectiveLanguage);

		runInAction(() => {
			// Always persist merged translations (bundled + auto-translated). A partial
			// language block in translations.json must not block caching the full set.
			this._cachedTranslations[effectiveLanguage] = translations;
			this._currentLanguage = effectiveLanguage;
		});
	};
	public get changeLanguage() {
		return this._changeLanguage;
	}
	public set changeLanguage(value) {
		this._changeLanguage = value;
	}

	autoTranslate = async (translations: Translations, toLang: string = this._currentLanguage, fromLang: string = DEFAULT_LANGUAGE) => {
		try {
			const result = await api.translations.autoTranslate(translations, toLang, fromLang);
			// Azure's model for some languages (e.g. `fil`) can return one shared bogus value
			// for many unrelated keys; revert those to English instead of showing wrong labels.
			const { cleaned, revertedKeys } = dropCollidingAutoTranslations(translations, sanitizeTranslations(result));
			if (revertedKeys.length > 0) {
				logger.logGeneral.error(
					{},
					`Auto-translation to "${toLang}" returned one shared value for ${revertedKeys.length} unrelated keys; falling back to English for those keys.`,
					revertedKeys.join(', ')
				);
			}
			return cleaned;
		} catch (error) {
			const msg = error instanceof Error ? error.message : error instanceof Array && error.length > 0 ? error[0] : 'Unknown';
			logger.logGeneral.error({}, `Error getting auto translations: ${msg}`, `${error}`);
			return {};
		}
	};

	get isConfigLoaded() {
		return this._isConfigLoaded;
	}

	get currentPageId() {
		return this._currentPageId;
	}

	setCurrentPageId = (id: string) => {
		this._currentPageId = id;
	};

	private getPage = (id: string) => {
		const page = this.config?.pages.find((page) => page.id === id);
		return page;
	};

	checkAndSetCashPayments = async () => {
		if (
			this.isConfigLoaded &&
			store.kioskStore.isDeviceSupported(SupportedDevices.CashAcceptor) &&
			store.kioskStore.canAcceptCashPayments === null
		) {
			await store.kioskStore.setCanTakeCashPayments();
			logger.debug.log(`canAcceptCashPayments set to ${store.kioskStore.canAcceptCashPayments}`);
		} else
			logger.debug.log(
				`canAcceptCashPayments not set. isLoaded: ${this.isConfigLoaded}, isDeviceSupported: ${store.kioskStore.isDeviceSupported(
					SupportedDevices.CashAcceptor
				)}, canAcceptCashPayments: ${store.kioskStore.canAcceptCashPayments}`
			);
	};
}
